/**
 * Tableau de bord.
 *
 * Il est organisé pour répondre, dans cet ordre, aux trois questions du produit :
 *
 * 1. Qu'est-ce que je dois faire maintenant ?
 * 2. Qu'est-ce qui arrive bientôt ?
 * 3. Est-ce que quelque chose risque de me surprendre ?
 */

import { useState } from "react";
import { Link, useNavigate } from "react-router-dom";

import { dashboardApi } from "../api/endpoints";
import { TaskList } from "../components/TaskItem";
import {
  Alert,
  Button,
  Card,
  EmptyState,
  ErrorAlert,
  LoadingBlock,
  PageHeader,
  Stat,
} from "../components/ui";
import { useDocumentTitle, useResource } from "../hooks/useApi";
import {
  formatAmount,
  formatDateTime,
  formatShortDate,
  pluralize,
} from "../lib/format";

export function DashboardPage() {
  useDocumentTitle("Tableau de bord");
  const navigate = useNavigate();
  const dashboard = useResource(() => dashboardApi.get(), []);
  const [horizon, setHorizon] = useState("d30");

  if (dashboard.loading) return <LoadingBlock />;
  if (dashboard.error) return <ErrorAlert error={dashboard.error} title="Tableau de bord indisponible" />;
  const data = dashboard.data;
  if (!data) return null;

  const counters = data.counters;
  const selected =
    data.horizons.find((bucket) => bucket.key === horizon) ?? data.horizons[0];

  // Les alertes d'un meme type se repetent vite (une par echeance depassee).
  // On en montre deux par type en tete, et on replie le reste : le tableau de
  // bord doit rester lisible, sans pour autant masquer une information.
  const seenPerKind = new Map<string, number>();
  const visibleAlerts: typeof data.alerts = [];
  const hiddenAlerts: typeof data.alerts = [];
  for (const alert of data.alerts) {
    const count = (seenPerKind.get(alert.kind) ?? 0) + 1;
    seenPerKind.set(alert.kind, count);
    (count <= 2 ? visibleAlerts : hiddenAlerts).push(alert);
  }

  return (
    <>
      <PageHeader
        title={data.business_name}
        subtitle={
          <>
            Situation au {formatDateTime(data.generated_at)}.{" "}
            {counters.total_open === 0
              ? "Aucune démarche en attente."
              : `${counters.total_open} démarche${counters.total_open > 1 ? "s" : ""} en cours de suivi.`}
          </>
        }
        actions={
          <>
            <Button variant="ghost" onClick={() => navigate("/demarches")}>
              Toutes les démarches
            </Button>
            <Button variant="primary" onClick={() => navigate("/demarches/nouvelle")}>
              Ajouter une démarche
            </Button>
          </>
        }
      />

      <div className="stack">
        {/* --- 3. Ce qui risque de surprendre : en tête, car c'est urgent --- */}
        {data.alerts.length > 0 && (
          <section className="stack stack--tight" aria-label="Alertes">
            {visibleAlerts.map((alert, index) => (
              <Alert
                key={`${alert.kind}-${index}`}
                tone={
                  alert.severity === "critical"
                    ? "critical"
                    : alert.severity === "warning"
                      ? "warning"
                      : "info"
                }
                title={alert.title}
                actions={
                  alert.action_url ? (
                    <Link to={alert.action_url} className="btn btn--sm btn--ghost">
                      Ouvrir
                    </Link>
                  ) : undefined
                }
              >
                {alert.message}
              </Alert>
            ))}
            {hiddenAlerts.length > 0 && (
              <details>
                <summary>
                  Afficher {pluralize(hiddenAlerts.length, "autre alerte", "autres alertes")}
                </summary>
                <div className="stack stack--tight mt-3">
                  {hiddenAlerts.map((alert, index) => (
                    <Alert
                      key={`${alert.kind}-more-${index}`}
                      tone={
                        alert.severity === "critical"
                          ? "critical"
                          : alert.severity === "warning"
                            ? "warning"
                            : "info"
                      }
                      title={alert.title}
                      actions={
                        alert.action_url ? (
                          <Link
                            to={alert.action_url}
                            className="btn btn--sm btn--ghost"
                          >
                            Ouvrir
                          </Link>
                        ) : undefined
                      }
                    >
                      {alert.message}
                    </Alert>
                  ))}
                </div>
              </details>
            )}
          </section>
        )}

        {/* --- Compteurs --------------------------------------------------- */}
        <section className="grid grid--2 grid--4" aria-label="Chiffres clés">
          <Stat
            value={counters.overdue}
            label="En retard"
            tone={counters.overdue > 0 ? "danger" : undefined}
            onClick={() => navigate("/demarches?tri=echeance")}
          />
          <Stat
            value={counters.next_7_days}
            label="Dans les 7 jours"
            tone={counters.next_7_days > 0 ? "warning" : undefined}
            onClick={() => navigate("/demarches")}
          />
          <Stat
            value={counters.in_progress + counters.waiting}
            label="En cours ou en attente"
            onClick={() => navigate("/demarches")}
          />
          {counters.awaited > 0 && (
            <Stat
              value={counters.awaited}
              label="À attendre et confirmer"
              tone={counters.awaited_delay_elapsed > 0 ? "warning" : undefined}
              onClick={() =>
                navigate("/demarches?only_expected_events=true")
              }
            />
          )}
          <Stat
            value={counters.validated_last_30_days}
            label="Validées (30 derniers jours)"
            tone="success"
            onClick={() => navigate("/historique")}
          />
        </section>

        <div className="grid grid--sidebar">
          <div className="stack">
            {/* --- 1. Qu'est-ce que je dois faire maintenant ? ------------- */}
            <Card
              title="À faire maintenant"
              hint="Démarches réalisables aujourd'hui, les plus prioritaires d'abord."
              flush
            >
              {data.now_tasks.length === 0 ? (
                <EmptyState title="Rien d'urgent pour le moment">
                  Aucune démarche n'appelle d'action immédiate. Les échéances à
                  venir sont listées ci-dessous.
                </EmptyState>
              ) : (
                <TaskList tasks={data.now_tasks} showWhy />
              )}
            </Card>

            {/* --- 2. Qu'est-ce qui arrive bientôt ? ----------------------- */}
            <Card
              title="Ce qui arrive bientôt"
              actions={
                <div className="chips" role="group" aria-label="Horizon">
                  {data.horizons.map((bucket) => (
                    <button
                      key={bucket.key}
                      type="button"
                      className="chip"
                      aria-pressed={bucket.key === horizon}
                      onClick={() => setHorizon(bucket.key)}
                    >
                      {bucket.label} ({bucket.count})
                    </button>
                  ))}
                </div>
              }
              flush
            >
              {!selected || selected.tasks.length === 0 ? (
                <EmptyState title="Aucune démarche sur cet horizon" />
              ) : (
                <TaskList tasks={selected.tasks} />
              )}
            </Card>

            {(data.in_progress.length > 0 || data.waiting.length > 0) && (
              <div className="grid grid--2">
                {data.in_progress.length > 0 && (
                  <Card title="En cours" flush>
                    <TaskList tasks={data.in_progress} showStatus={false} />
                  </Card>
                )}
                {data.waiting.length > 0 && (
                  <Card
                    title="En attente"
                    hint="Vous attendez un tiers."
                    flush
                  >
                    <TaskList tasks={data.waiting} showStatus={false} />
                  </Card>
                )}
              </div>
            )}

            {data.recently_validated.length > 0 && (
              <Card
                title="Validées récemment"
                hint="Conservées dans votre historique."
                flush
              >
                <TaskList tasks={data.recently_validated} showStatus={false} />
              </Card>
            )}
          </div>

          {/* --- Colonne latérale ---------------------------------------- */}
          <div className="stack">
            <Card
              title="Paiements à anticiper"
              hint="Montants connus ou estimés rattachés à vos démarches."
            >
              {data.upcoming_payments.length === 0 ? (
                <p className="text-muted mb-0">
                  Aucun paiement administratif identifié pour l'instant.
                </p>
              ) : (
                <>
                  <ul className="stack stack--tight" style={{ listStyle: "none", padding: 0, margin: 0 }}>
                    {data.upcoming_payments.slice(0, 6).map((payment) => (
                      <li key={payment.task_id}>
                        <Link to={`/demarches/${payment.task_id}`}>
                          {payment.title}
                        </Link>
                        <div className="inline-note">
                          {payment.amount_kind === "known"
                            ? formatAmount(payment.amount_cents, payment.amount_currency)
                            : payment.amount_kind_label}
                          {" · "}
                          {payment.payment_due_on || payment.due_on
                            ? formatShortDate(payment.payment_due_on ?? payment.due_on)
                            : "date à préciser"}
                        </div>
                      </li>
                    ))}
                  </ul>
                  {data.payments_total_known_cents > 0 && (
                    <p className="mt-4 mb-0">
                      <strong>
                        Total des montants connus :{" "}
                        {formatAmount(
                          data.payments_total_known_cents,
                          data.payments_currency,
                        )}
                      </strong>
                      <br />
                      <span className="inline-note">
                        Les montants estimés ou inconnus ne sont pas additionnés.
                      </span>
                    </p>
                  )}
                </>
              )}
            </Card>

            {data.missing_profile_fields.length > 0 && (
              <Card title="Compléter votre profil">
                <p className="text-muted">
                  {data.missing_profile_fields.length === 1
                    ? "Cette information manque pour déterminer une obligation :"
                    : "Ces informations manquent pour déterminer certaines obligations :"}
                </p>
                <ul>
                  {data.missing_profile_fields.map((field) => (
                    <li key={field}>{field}</li>
                  ))}
                </ul>
                <Link to="/profil-entreprise" className="btn btn--ghost btn--sm">
                  Compléter le profil
                </Link>
              </Card>
            )}

            <Card title="Fiabilité des informations">
              <p className="text-muted">
                {data.needs_verification_count > 0 ? (
                  <>
                    {data.needs_verification_count} point
                    {data.needs_verification_count > 1 ? "s" : ""} de vigilance
                    n'{data.needs_verification_count > 1 ? "ont" : "a"} pas pu être
                    confirmé sur une source officielle. Ils ne sont pas présentés
                    comme des obligations certaines.
                  </>
                ) : (
                  "Toutes les règles actives citent une source officielle vérifiée."
                )}
              </p>
              <Link to="/bibliotheque" className="btn btn--ghost btn--sm">
                Voir les sources
              </Link>
              {data.rules_library_version && (
                <p className="inline-note mt-3 mb-0">
                  Bibliothèque administrative version {data.rules_library_version}.
                </p>
              )}
            </Card>
          </div>
        </div>
      </div>
    </>
  );
}
